"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useWishlist, TypeWishlistCar } from "@context/WishlistContext";
import { useWishlistToast } from "@hooks/useWishlistToast";

interface UseWishlistActionsProps {
  slug?: string;
  brandName?: string;
  modelName?: string;
  isLoggedIn?: boolean;
}

export const useWishlistActions = ({
  slug,
  brandName,
  modelName,
  isLoggedIn = false,
}: UseWishlistActionsProps) => {
  const router = useRouter();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [pendingWishlistCar, setPendingWishlistCar] =
    useState<TypeWishlistCar | null>(null);

  const {
    wishlistItems,
    toggleWishlistItem,
    replaceWishlistItem,
    isCarWishlisted,
  } = useWishlist();
  const { toast, showToast, hideToast } = useWishlistToast();

  // Only show wishlist if we have the required car data
  const showWishlist = slug && brandName && modelName;

  const car: TypeWishlistCar | undefined = showWishlist
    ? {
        id: slug,
        make: brandName,
        name: modelName,
      }
    : undefined;

  // Wishlist functionality
  const handleWishlistToggle = async (
    brand_name: string,
    model_name: string,
    car: TypeWishlistCar
  ) => {
    const isCurrentlyWishlisted = isCarWishlisted(car.id);

    if (isCurrentlyWishlisted) {
      const result = await toggleWishlistItem(car);
      if (result.success) {
        showToast(car, false);
      }
      // TODO: Handle removal failure case if needed
    } else {
      const result = await toggleWishlistItem(car);
      if (result.success) {
        showToast(car, true);
      } else if (result.limitReached) {
        setPendingWishlistCar(car);
        setShowLimitModal(true);
      }
      // TODO: Handle other failure cases (network errors, etc.)
    }
  };

  const handleUndo = async () => {
    if (toast.lastAction && toast.show) {
      const carToUndo = wishlistItems.find(
        (item) => item.name === toast.lastAction?.carName
      );
      if (carToUndo) {
        const result = await toggleWishlistItem(carToUndo);
        if (result.success) {
          hideToast();
        }
      }
    }
  };

  const handleViewWishlist = () => {
    hideToast();
    router.push("/wishlist");
  };

  const handleReplace = async (oldCarId: string, newCar: TypeWishlistCar) => {
    const success = await replaceWishlistItem(oldCarId, newCar);
    if (success) {
      showToast(newCar, true);
      setShowLimitModal(false);
      setPendingWishlistCar(null);
    }
  };

  const closeLimitModal = () => {
    setShowLimitModal(false);
    setPendingWishlistCar(null);
  };

  return {
    // State
    showLimitModal,
    pendingWishlistCar,
    showWishlist,
    car,
    toast,
    wishlistItems,

    // Functions
    handleWishlistToggle,
    handleUndo,
    handleViewWishlist,
    handleReplace,
    closeLimitModal,
    isCarWishlisted,
  };
};
